import { FinancingApi, LoanApplicationSubmitSource2 } from '@servicetitan/financing-api-client';
import { SetupData, SetupDataStore } from './setupDataStore';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

type Role = 'homeowner' | 'technician';

export const useSetSetupData = ({
    role,
    provider,
    tenantId,
    buId,
    technicianId,
    jobId,
    customerId,
    state,
    source,
    useInAppBrowser,
    prequalificationId,
    preApprovalId,
    isApplyingForPrequalification,
    loanId,
    referrer,
    returnUrl,
}: Omit<SetupData, 'provider'> & {
    role: Role;
    provider?: string;
    source?: LoanApplicationSubmitSource2;
    prequalificationId?: string;
    isApplyingForPrequalification?: boolean;
}) => {
    const { setSetupData, setupData } = SetupDataStore.useStore();
    const [error, setError] = useState<Error | null>(null);
    const [apiClient] = useDependencies(FinancingApi);

    // Query for tenant settings; only needed for homeowners.
    const settingsResult = useQuery({
        queryKey: ['tenantSettings', tenantId],
        queryFn: () => apiClient.tenantsGetSettings(tenantId),
        enabled: role === 'homeowner' && !provider && !setupData?.isInSecondLook,
    });

    // Query for provider preferences; only needed for technicians when no provider is available in the props.
    const providerResult = useQuery({
        queryKey: ['tenantsProviderPreferences', tenantId],
        queryFn: () => apiClient.tenantsGetAllProviderPreferences({ tenantId }),
        enabled: role === 'technician' && !provider && !setupData?.isInSecondLook,
    });

    const settings = settingsResult.data?.data;
    const providerPreferences = providerResult.data?.data;

    useEffect(() => {
        // Skip automatic setup if provider was manually set
        if (setupData?.isInSecondLook) {
            return;
        }

        // If the provider is already set, don't override it
        if (setupData?.provider) {
            return;
        }

        let resolvedProvider: string = '';

        // Use provided override first, regardless of role
        if (provider) {
            resolvedProvider = provider;
        } else {
            // Fallback to role-specific provider resolution
            if (role === 'homeowner') {
                if (!settingsResult.isSuccess) {
                    return;
                }
                if (
                    !settings?.allowedOnlineEstimatesProviders ||
                    settings.allowedOnlineEstimatesProviders.length === 0
                ) {
                    setError(new Error('No allowed online estimates providers'));
                    return;
                }
                resolvedProvider = settings.allowedOnlineEstimatesProviders[0];
            } else if (role === 'technician') {
                if (!providerResult.isSuccess) {
                    return;
                }
                if (!Array.isArray(providerPreferences) || providerPreferences.length === 0) {
                    setError(new Error('Financing is not configured for this tenant'));
                    return;
                }
                const preferredProvider = providerPreferences.find(p => p.preferred);
                if (!preferredProvider) {
                    setError(new Error('Preferred provider is not set'));
                    return;
                }
                resolvedProvider = preferredProvider.providerName!;
            }
        }

        setSetupData({
            provider: resolvedProvider,
            calculationsRequestProvider: resolvedProvider,
            tenantId,
            buId,
            technicianId,
            jobId,
            customerId,
            state,
            source,
            useInAppBrowser,
            role,
            prequalificationId,
            preApprovalId,
            isApplyingForPrequalification,
            loanId,
            referrer,
            returnUrl,
        });

        // Disable exhaustive deps warning. The only real dependencies here are the results of the
        // settings query, the provider preferences query, and the provider prop.
        //
        // Everything else either won't change during the application (e.g. jobId, role, tenantId, etc.),
        // or is there just to prevent inadvertently overwriting user's choices (e.g. setupData.provider)
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.allowedOnlineEstimatesProviders, providerPreferences, provider]);

    // Throw error during render cycle so it can be caught by error boundaries
    if (error) {
        throw error;
    }

    // Return the appropriate query result based on role
    return role === 'homeowner' ? settingsResult : providerResult;
};
